import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import { classNames } from '../utils/themeHelpers';
import LicenseGate from '../components/ui/LicenseGate';
import { ChartGuideModal } from '../components/ui';
import useChartGuide from '../hooks/useChartGuide';
import { TYPOGRAPHY_CONFIG } from '../config/typography.config';
import { HeatMap, PerformanceSummary, PerformanceTrendAnalysis } from '../components/customerClubHeatmap';
import { ClubMetrics } from '../types/club';
import { TimePeriod } from '../types/api';
import { useClub12MonthAverage } from '../hooks/useClubQueries';

interface Brand {
  id: number;
  name: string;
  logo?: string;
}

interface Branch {
  id: number;
  name: string;
  brandId: number;
  logo?: string;
}

interface Props {
  selectedBranch: Branch | null;
  selectedBrand: Brand | null;
  selectedPeriod?: TimePeriod;
}

// Custom Metric Dropdown Component (same as original)
const MetricDropdown: React.FC<{
  metrics: Array<{ id: string; name: string }>;
  selectedMetric: string;
  onSelect: (value: string) => void;
}> = ({ metrics, selectedMetric, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const selectRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Find selected metric
  const selectedMetricData = metrics.find(m => m.id === selectedMetric);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInsideSelect = selectRef.current && selectRef.current.contains(target);
      const isClickInsideDropdown = target && (target as HTMLElement).closest('.metric-dropdown-menu');
      
      if (!isClickInsideSelect && !isClickInsideDropdown) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex(prev =>
          prev < metrics.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < metrics.length) {
          onSelect(metrics[highlightedIndex].id);
          setIsOpen(false);
          setHighlightedIndex(-1);
        }
        break;
    }
  };

  // Scroll to highlighted option
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRef.current) {
      const highlightedElement = optionsRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  return (
    <>
      <div ref={selectRef} className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          className="px-3 py-1.5 rounded-lg border-2 border-border-secondary bg-bg-secondary text-text-primary text-xs font-medium focus:outline-none focus:ring-2 focus:ring-accent-purple focus:border-accent-purple transition-all w-full appearance-none pr-10 text-right hover:bg-bg-tertiary hover:border-border-primary"
          aria-label="انتخاب شاخص"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          {selectedMetricData?.name || 'انتخاب شاخص'}
        </button>
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg className="w-4 h-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && ReactDOM.createPortal(
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999998,
              backgroundColor: 'transparent',
            }}
            onClick={() => {
              setIsOpen(false);
              setHighlightedIndex(-1);
            }}
          />
          <div
            className="metric-dropdown-menu"
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              backgroundColor: 'var(--surface-elevated)',
              border: '1px solid var(--border-muted)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              zIndex: 9999999,
              width: `${dropdownPosition.width}px`,
              minWidth: '200px',
              maxWidth: '90vw',
              overflow: 'hidden',
            }}
          >
            {/* Options List */}
            <div
              ref={optionsRef}
              role="listbox"
              style={{
                maxHeight: '240px',
                padding: '8px',
                overflowY: 'auto',
                direction: 'ltr',
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--border-muted) transparent',
              }}
              className="custom-scrollbar"
            >
              {metrics.map((metric, index) => (
                <div
                  key={metric.id}
                  role="option"
                  aria-selected={selectedMetric === metric.id}
                  onClick={() => {
                    onSelect(metric.id);
                    setIsOpen(false);
                    setHighlightedIndex(-1);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    direction: 'rtl',
                    padding: '10px 6px',
                    margin: '2px 4px',
                    borderRadius: 'var(--button-border-radius)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease-in-out',
                    backgroundColor:
                      highlightedIndex === index
                        ? 'var(--surface-secondary)'
                        : 'transparent',
                    minHeight: '36px',
                  }}
                >
                  <div
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      border: `2px solid ${
                        selectedMetric === metric.id
                          ? 'var(--color-primary-500)'
                          : 'var(--border-muted)'
                      }`,
                      backgroundColor:
                        selectedMetric === metric.id
                          ? 'var(--color-primary-500)'
                          : 'transparent',
                      marginRight: '12px',
                      flexShrink: 0,
                      position: 'relative',
                      marginLeft:'12px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {selectedMetric === metric.id && (
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                        }}
                      />
                    )}
                  </div>
                  <span
                    style={{
                      color: 'var(--content-primary)',
                      fontSize: `${TYPOGRAPHY_CONFIG.fontSizes.sm}px`,
                      flex: 1,
                      lineHeight: '1.5',
                      textAlign: 'right',
                      marginLeft: '12px',
                    }}
                  >
                    {metric.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: var(--border-muted);
          border-radius: 3px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: var(--content-tertiary);
        }
      `}</style>
    </>
  );
};

const CustomerClubHeatmapDashboard: React.FC<Props> = ({ 
  selectedBranch, 
  selectedBrand, 
  selectedPeriod
}) => {
  const { currentTheme } = useTheme();
  const { apiTimeFrame } = useTimeFrame();
  const { isModalOpen, currentGuide, closeGuide } = useChartGuide();
  
  // Use selectedPeriod if provided, otherwise use the one from context
  const effectiveTimeRange = selectedPeriod || apiTimeFrame;
  
  // Fetch 12-month average data
  const { data: average12MonthData, isLoading: isLoading12Month } = useClub12MonthAverage(selectedBranch?.id);
  
  // Define metrics for the heatmap
  const metrics = [
    { id: "subscription", name: "اشتراک‌گیری", unit: "%", type: "percent", isSelected: true },
    { id: "avgInvoice", name: "میانگین سفارش", unit: "ریال", type: "currency", isSelected: false },
    { id: "avgItemPrice", name: "میانگین قیمت هر کالا", unit: "ریال", type: "currency", isSelected: false },
    { id: "avgItemCount", name: "میانگین تعداد اقلام در سفارش", unit: "", type: "count", isSelected: false },
    { id: "creditPercent", name: "درصد سهم سفارش‌های با اعتبار", unit: "%", type: "percent", isSelected: false },
    { id: "uniqueCustomers", name: "مشتریان یکتا", unit: "", type: "count", isSelected: false }
  ];

  // State for selected metric in the heatmap
  const [selectedMetric, setSelectedMetric] = useState<string>("subscription");
  // State for detailed view month
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  // State for selected month data
  const [selectedMonthData, setSelectedMonthData] = useState<ClubMetrics | null>(null);
  // State for showing brand group line
  const [showBrandLine, setShowBrandLine] = useState(false);
  // State for showing all restaurants line  
  const [showAllRestaurantsLine, setShowAllRestaurantsLine] = useState(false);
  // State for showing 12 months average line
  const [show12MonthsAverageLine, setShow12MonthsAverageLine] = useState(false);
  // State for comparison source (all restaurants, brand group, or 12 months average) - used for heatmap
  const [comparisonSource, setComparisonSource] = useState<'brand' | 'all' | 'average12'>('brand');

  // Function to handle metric selection
  const handleMetricSelect = (metricId: string) => {
    setSelectedMetric(metricId);
    setSelectedMonth(null);
  };

  // Function to handle comparison source change
  const handleComparisonSourceChange = (source: string) => {
    setComparisonSource(source as 'brand' | 'all' | 'average12');
  };

  // Function to toggle brand line
  const toggleBrandLine = () => {
    setShowBrandLine(!showBrandLine);
  };

  // Function to toggle 12 months average line
  const toggle12MonthsAverageLine = () => {
    setShow12MonthsAverageLine(!show12MonthsAverageLine);
  };

  // Function to handle month selection from heatmap
  const handleMonthSelect = (month: string, data: ClubMetrics | null) => {
    setSelectedMonth(month);
    setSelectedMonthData(data);
  };

  return (
    <LicenseGate licenseType="club" dashboardName="نقشه حرارتی باشگاه مشتریان">
      <div dir="rtl" className="container-dashboard">
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          {/* Professional Dashboard Header */}
          <h1 className="title-dashboard">
            نقشه حرارتی باشگاه مشتریان
          </h1>
          <p className="text-sm text-text-secondary -mt-6">
            شدت رنگ‌ها نشان‌دهنده‌ی مقدار هر شاخص در هر ماه است.
          </p>

          {/* Dashboard Controls - Three Separate Controllers Side by Side */}
          <div id="dashboard-controls" className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-6" aria-label="کنترل‌های داشبورد">
            {/* Controller 1: Metric Selector */}
            <div className="rounded-xl bg-bg-secondary/60 backdrop-blur-sm border border-border-primary shadow-sm p-4">
              <div className="flex flex-col gap-2">
                <label htmlFor="metric-selector" className="text-xs font-semibold text-text-primary uppercase tracking-wider">انتخاب شاخص</label>
                <MetricDropdown
                  metrics={metrics}
                  selectedMetric={selectedMetric}
                  onSelect={handleMetricSelect}
                />
              </div>
            </div>

            {/* Controller 2: Comparison Source */}
            <div className="rounded-xl bg-bg-secondary/60 backdrop-blur-sm border border-border-primary shadow-sm p-4">
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold text-text-primary uppercase tracking-wider">مقایسه با</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleComparisonSourceChange('brand')}
                    className={classNames(
                      "px-3 py-1.5 rounded-lg border-2 text-xs font-medium transition-colors shadow-sm flex-1",
                      comparisonSource === 'brand' 
                        ? "border-status-warning bg-status-warning text-text-inverse shadow-lg" 
                        : "border-border-primary bg-bg-elevated text-text-primary hover:bg-bg-secondary hover:border-status-warning"
                    )}
                    aria-pressed={comparisonSource === 'brand'}
                  >
                    <span className="flex items-center justify-center gap-1">
                      {comparisonSource === 'brand' && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      گروه {selectedBrand?.name || 'برند'}
                    </span>
                  </button>
                  <button
                    onClick={() => handleComparisonSourceChange('average12')}
                    className={classNames(
                      "px-3 py-1.5 rounded-lg border-2 text-xs font-medium transition-colors shadow-sm flex-1",
                      comparisonSource === 'average12' 
                        ? "border-status-info bg-status-info text-text-inverse shadow-lg" 
                        : "border-border-primary bg-bg-elevated text-text-primary hover:bg-bg-secondary hover:border-status-info"
                    )}
                    aria-pressed={comparisonSource === 'average12'}
                  >
                    <span className="flex items-center justify-center gap-1">
                      {comparisonSource === 'average12' && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      میانگین ۱۲ ماه
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Controller 3: Chart Options */}
            <div className="rounded-xl bg-bg-secondary/60 backdrop-blur-sm border border-border-primary shadow-sm p-4">
              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold text-text-primary uppercase tracking-wider">خطوط نمودار</span>
                <div className="flex gap-2">
                  <button
                    onClick={toggleBrandLine}
                    className={classNames(
                      "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shadow-sm flex-1",
                      showBrandLine 
                        ? "bg-status-success text-text-inverse shadow-lg" 
                        : "bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary"
                    )}
                    aria-pressed={showBrandLine}
                    aria-label="نمایش/مخفی کردن خط برند"
                  >
                    <span className="flex items-center justify-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 16 16">
                        <path d="M2 8h12M2 4l4 4-4 4M14 4l-4 4 4 4" />
                      </svg>
                      خط برند
                    </span>
                  </button>
                  <button
                    onClick={toggle12MonthsAverageLine}
                    className={classNames(
                      "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shadow-sm flex-1",
                      show12MonthsAverageLine 
                        ? "bg-status-info text-text-inverse shadow-lg" 
                        : "bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary"
                    )}
                    aria-pressed={show12MonthsAverageLine}
                    aria-label="نمایش/مخفی کردن خط میانگین ۱۲ ماه"
                  >
                    <span className="flex items-center justify-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 16 16">
                        <path d="M2 8h12M2 12l4-4 4 4 4-4M2 4l4 4 4-4 4 4" />
                      </svg>
                      میانگین ۱۲ ماه
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Professional Heat Map Container */}
            <div id="heatmap" className="lg:col-span-2 order-2 lg:order-1 space-y-6 md:space-y-8">
              <HeatMap
                branchId={selectedBranch?.id}
                brandId={selectedBrand?.id}
                timeRange={effectiveTimeRange}
                selectedMetric={selectedMetric}
                comparisonSource={comparisonSource}
                onMonthSelect={handleMonthSelect}
                metrics={metrics}
                average12MonthData={average12MonthData?.averages}
              />
              
              <PerformanceTrendAnalysis
                branchId={selectedBranch?.id}
                brandId={selectedBrand?.id}
                branchName={selectedBranch?.name}
                brandName={selectedBrand?.name}
                timeRange={effectiveTimeRange}
                selectedMetric={selectedMetric}
                showBrandLine={showBrandLine}
                showAllRestaurantsLine={showAllRestaurantsLine}
                show12MonthsAverageLine={show12MonthsAverageLine}
                metrics={metrics}
                average12MonthData={average12MonthData?.averages}
              />
            </div>

            {/* Professional Analysis Panel */}
            <div id="monthly-details" className="card-base order-1 lg:order-2">
              <PerformanceSummary
                branchId={selectedBranch?.id}
                brandId={selectedBrand?.id}
                timeRange={effectiveTimeRange}
                selectedMonth={selectedMonth}
                selectedMonthData={selectedMonthData}
              />
            </div>
          </div>
        </div>
        
        {currentGuide && (
          <ChartGuideModal
            isOpen={isModalOpen}
            onClose={closeGuide}
            title={currentGuide.title}
          >
            <div className="space-y-8">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">توضیحات</h3>
                <p className="text-text-secondary">{currentGuide.description}</p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نحوه تفسیر</h3>
                <p className="text-text-secondary">{currentGuide.interpretation}</p>
              </div>
              {currentGuide.tips.length > 0 && (
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-2">نکات کلیدی</h3>
                  <ul className="list-disc list-inside space-y-2">
                    {currentGuide.tips.map((tip, index) => (
                      <li key={index} className="text-text-secondary">{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </ChartGuideModal>
        )}
      </div>
    </LicenseGate>
  );
};

export default CustomerClubHeatmapDashboard;